import React, { useState, useRef } from "react";
import { Upload, FileCheck2, AlertCircle, X, ExternalLink } from "lucide-react";
import { BusyButton } from "./BusyButton";
import { Button } from "../ui/button";
import { formatQty } from "../../lib/format";
import { apiErrorMessage } from "../../lib/inventoryApi";

// ============================================================================
// VendorProofUploader — pengunggah bukti transfer VENDOR (#4b-3b).
//
// NOL KONSTANTA DI FILE INI. Batas ukuran, daftar MIME, dan umur signed URL
// SEMUANYA dibaca dari blok `proof` milik /meta/payment (atau /meta/dp —
// backend menyatakan keduanya berbagi aturan yang sama lewat
// `shared_with_payments` / `shared_with_dp`). Kalau backend menaikkan batas
// dari 25 MB, layar ini ikut tanpa disentuh.
//
// KENAPA BUKAN components/etams/ProofUploader.jsx yang dipakai ulang:
//   1. Ia menembak POST /payments/upload-proof-file — bukti bayar CUSTOMER,
//      arah uangnya berlawanan dan region-nya diturunkan dari entitas lain.
//   2. Ia MENYALIN MAX_BYTES + daftar MIME ke JS (baris 5-6), persis yang
//      dilarang di header purchasingApi.js.
//   3. Warnanya literal (slate/emerald/rose) — rusak di mode gelap.
// Ketiganya bukan hal yang bisa ditambal dengan prop.
//
// DIRANCANG UNTUK DIPAKAI ULANG oleh /dp/{id}/pay (#4b-3c): pemanggil yang
// memutuskan cara mengunggah lewat `uploadFn`, komponen ini tidak tahu-menahu
// soal invoice maupun DP. Yang dijanjikannya cuma satu: balikan `uploadFn`
// membawa `proof_file_id`.
// ============================================================================

/**
 * @param {object|null} proofMeta  blok `proof` dari /meta/payment | /meta/dp.
 *        Selama masih null, validasi sisi klien DIMATIKAN (backend tetap
 *        menolak) — lebih baik satu bolak-balik daripada menebak batasnya.
 * @param {(file: File) => Promise<object>} uploadFn  mengunggah, mengembalikan
 *        payload berisi proof_file_id + signed_url.
 * @param {(payload: object|null) => void} onUploaded  dipanggil setelah
 *        berhasil, dan dengan null saat berkas dibersihkan.
 */
export function VendorProofUploader({
  proofMeta, uploadFn, onUploaded, disabled = false,
  testIdPrefix = "vendor-proof",
}) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [hasil, setHasil] = useState(null);
  const inputRef = useRef(null);

  const mimes = proofMeta?.allowed_mime_types || null;
  const maxBytes = proofMeta?.max_bytes ?? null;
  const maxMB = maxBytes ? formatQty(Math.round(maxBytes / 1024 / 1024), "MB") : null;
  const umurMenit = proofMeta?.signed_url_expires_in_sec
    ? Math.round(proofMeta.signed_url_expires_in_sec / 60) : null;

  const bytesLabel = (n) =>
    n >= 1024 * 1024
      ? formatQty(Math.round((n / 1024 / 1024) * 100) / 100, "MB")
      : formatQty(Math.round(n / 1024), "KB");

  const pilih = (e) => {
    setErr(null);
    setHasil(null);
    onUploaded?.(null);
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    // Pemeriksaan sisi klien hanya kalau aturannya sudah diketahui dari meta.
    if (mimes && !mimes.includes(f.type)) {
      setErr(
        `Jenis berkas ${f.type || "(tidak dikenali)"} tidak diizinkan. `
        + `Yang diterima: ${mimes.join(", ")}.`
      );
      setFile(null);
      e.target.value = "";
      return;
    }
    if (maxBytes && f.size > maxBytes) {
      setErr(
        `Berkas ${bytesLabel(f.size)} melebihi batas ${maxMB}. `
        + `Perkecil dulu berkasnya lalu coba lagi.`
      );
      setFile(null);
      e.target.value = "";
      return;
    }
    if (f.size === 0) {
      setErr("Berkas kosong. Pilih berkas bukti transfer yang benar.");
      setFile(null);
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const unggah = async () => {
    if (!file || !uploadFn) return;
    setBusy(true);
    setErr(null);
    try {
      const payload = await uploadFn(file);
      setHasil(payload);
      onUploaded?.(payload);
    } catch (e) {
      setErr(apiErrorMessage(e, "Gagal mengunggah bukti transfer."));
    } finally {
      setBusy(false);
    }
  };

  const bersihkan = () => {
    setFile(null);
    setErr(null);
    setHasil(null);
    if (inputRef.current) inputRef.current.value = "";
    onUploaded?.(null);
  };

  return (
    <div className="rounded border border-dashed border-border bg-muted/40 p-3"
         data-testid={`${testIdPrefix}-root`}>
      <div className="flex items-center gap-2 mb-2">
        <Upload className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium">
          Bukti transfer{proofMeta?.required ? " (wajib)" : ""}
        </span>
      </div>

      {!hasil && (
        <div className="space-y-2">
          <input
            ref={inputRef}
            type="file"
            accept={mimes ? mimes.join(",") : undefined}
            disabled={disabled || busy}
            onChange={pilih}
            data-testid={`${testIdPrefix}-input`}
            className="block w-full text-sm text-foreground file:mr-3 file:py-1.5 file:px-3
                       file:rounded file:border file:border-border file:bg-background
                       file:text-sm file:font-medium file:text-foreground
                       file:cursor-pointer hover:file:bg-muted"
          />
          <p className="text-xs text-muted-foreground">
            {mimes
              ? `Diterima: ${mimes.join(", ")}${maxMB ? ` · maksimal ${maxMB}` : ""}.`
              : "Memuat aturan berkas dari server..."}
            {proofMeta?.one_proof_per_payment
              && " Satu berkas untuk satu pembayaran — berkas yang sudah dipakai akan ditolak."}
          </p>
          {file && (
            <div className="flex items-center justify-between gap-2 rounded border border-border
                            bg-background px-2 py-1.5 text-xs">
              <span className="truncate" data-testid={`${testIdPrefix}-filename`}>
                {file.name} · {bytesLabel(file.size)}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <BusyButton size="sm" busy={busy} busyText="Mengunggah..."
                            disabled={disabled} onClick={unggah}
                            data-testid={`${testIdPrefix}-submit`}>
                  Unggah
                </BusyButton>
                <Button type="button" variant="ghost" size="sm" disabled={busy}
                        onClick={bersihkan} data-testid={`${testIdPrefix}-clear`}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {hasil && (
        <div className="flex items-start gap-2 rounded border border-success/40 bg-success-soft
                        px-3 py-2 text-success-soft-foreground"
             data-testid={`${testIdPrefix}-result`}>
          <FileCheck2 className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1 text-xs">
            <div className="font-medium truncate">
              Terunggah: {hasil.filename || "bukti transfer"}
            </div>
            <div>
              {hasil.size_bytes ? bytesLabel(hasil.size_bytes) : ""}
              {hasil.mime_type ? ` · ${hasil.mime_type}` : ""}
            </div>
            {hasil.signed_url && (
              <a href={hasil.signed_url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 underline underline-offset-2 mt-1"
                 data-testid={`${testIdPrefix}-link`}>
                <ExternalLink className="h-3.5 w-3.5" /> Buka bukti
                {umurMenit ? ` (tautan berlaku ${umurMenit} menit)` : ""}
              </a>
            )}
          </div>
          <Button type="button" variant="ghost" size="sm" disabled={busy || disabled}
                  onClick={bersihkan} data-testid={`${testIdPrefix}-reset`}>
            Ganti
          </Button>
        </div>
      )}

      {err && (
        <div className="mt-2 flex items-start gap-2 rounded border border-danger/40
                        bg-danger-soft px-2 py-1.5 text-xs text-danger-soft-foreground"
             data-testid={`${testIdPrefix}-error`}>
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span className="break-words">{err}</span>
        </div>
      )}
    </div>
  );
}

export default VendorProofUploader;
